<template>
  <a-modal
    v-model:visible="visible"
    :title="t('workspaceSettings.members.inviteTitle')"
    :width="500"
    :ok-loading="okLoading"
    :ok-button-props="{ disabled: !selectedUser }"
    :on-before-ok="handleInvite"
    @cancel="handleCancel"
  >
    <a-form :model="inviteForm" layout="vertical">
      <a-form-item :label="t('workspaceSettings.members.email')">
        <a-input
          v-model="inviteForm.email"
          :placeholder="t('workspaceSettings.members.emailPlaceholder')"
          :loading="searching"
          @press-enter="handleSearchUser"
        >
          <template #suffix>
            <a-button
              type="primary"
              size="small"
              :loading="searching"
              @click="handleSearchUser"
            >
              <template #icon>
                <icon-search />
              </template>
            </a-button>
          </template>
        </a-input>
      </a-form-item>

      <!-- 用户搜索结果 -->
      <a-form-item v-if="searchError" label="User">
        <div class="user-search-result error">
          <icon-close-circle :size="20" />
          <span>{{ searchError }}</span>
        </div>
      </a-form-item>

      <a-form-item v-else-if="selectedUser" label="User">
        <div class="user-search-result selected">
          <a-avatar :size="32" style="background-color: #165dff; color: #fff">
            {{
              (selectedUser.email || selectedUser.nickname || 'U')
                .slice(0, 1)
                .toUpperCase()
            }}
          </a-avatar>
          <div class="user-info">
            <div class="user-name">
              {{ selectedUser.nickname || selectedUser.email }}
            </div>
            <div class="user-email">{{ selectedUser.email }}</div>
          </div>
          <a-button type="text" size="small" @click="handleRemoveSelectedUser">
            <template #icon>
              <icon-close />
            </template>
          </a-button>
        </div>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Message from '@/utils/message';
  import {
    IconSearch,
    IconClose,
    IconCloseCircle,
  } from '@arco-design/web-vue/es/icon';
  import { searchUsersApi } from '@/api/user';

  const { t } = useI18n();

  const props = defineProps<{
    modelValue: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'invite', user: any): Promise<void>;
    (e: 'success'): void;
  }>();

  const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const inviteForm = ref({
    email: '',
  });
  const searching = ref(false);
  const selectedUser = ref<any>(null);
  const searchError = ref('');
  const okLoading = ref(false);

  const handleSearchUser = async () => {
    if (!inviteForm.value.email) {
      Message.warning(
        t('workspaceSettings.members.emailRequired') || '请输入邮箱'
      );
      return;
    }

    searching.value = true;
    searchError.value = '';
    selectedUser.value = null;

    try {
      const searchRes = await searchUsersApi({ email: inviteForm.value.email });

      const results = searchRes.data?.user || null;
      if (!results) {
        searchError.value =
          t('workspaceSettings.members.userNotFound') ||
          'No user found with the email. Please check, or the user may not be registered yet.';
        return;
      }

      selectedUser.value = results;
    } catch (err: any) {
      searchError.value =
        err?.response?.data?.msg ||
        t('workspaceSettings.members.searchFailed') ||
        '搜索用户失败';
    } finally {
      searching.value = false;
    }
  };

  const handleRemoveSelectedUser = () => {
    selectedUser.value = null;
    searchError.value = '';
  };

  const handleCancel = () => {
    visible.value = false;
    inviteForm.value.email = '';
    selectedUser.value = null;
    searchError.value = '';
  };

  const handleInvite = async () => {
    if (!selectedUser.value) {
      Message.warning(
        t('workspaceSettings.members.pleaseSelectUser') || '请先搜索并选择用户'
      );
      return false;
    }

    try {
      okLoading.value = true;
      await emit('invite', selectedUser.value);
      emit('success');
      handleCancel();
      return true;
    } catch (err: any) {
      Message.error(
        err?.response?.data?.msg ||
          t('workspaceSettings.members.inviteFailed') ||
          '邀请失败'
      );
      return false;
    } finally {
      okLoading.value = false;
    }
  };

  // 监听弹窗关闭，重置状态
  watch(visible, (newVal) => {
    if (!newVal) {
      inviteForm.value.email = '';
      selectedUser.value = null;
      searchError.value = '';
    }
  });
</script>

<script lang="ts">
  export default {
    name: 'InviteMemberModal',
  };
</script>

<style lang="less" scoped>
  .user-search-result {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid var(--color-border-2);
    width: 100%;

    &.error {
      background: var(--color-danger-light-1);
      border-color: var(--color-danger-light-3);
      color: var(--color-danger-6);

      .arco-icon {
        color: var(--color-danger-6);
      }
    }

    &.selected {
      background: var(--color-bg-2);
      border-color: var(--color-primary-light-3);

      .user-info {
        flex: 1;

        .user-name {
          font-size: 14px;
          font-weight: 500;
          color: var(--color-text-1);
          margin-bottom: 4px;
        }

        .user-email {
          font-size: 13px;
          color: var(--color-text-3);
        }
      }
    }
  }
</style>
